import {useEffect, useState} from 'react'
import {useQuery, useMutation} from '@apollo/client'
import {Table, Button, Form, Spinner, Card} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {
  GET_ONBOARDING_CONFIG_BY_ORGANIZATION,
  GET_SIGNATURE_FORMS_BY_ORGANIZATION,
} from '../../../gql/queries/organizationOnboardingQuery'
import {
  CREATE_ONBOARDING_STEP_MESSAGE,
  UPDATE_ONBOARDING_STEP_MESSAGE,
  DELETE_ONBOARDING_STEP_MESSAGE,
  CREATE_REQUIRED_DOCUMENT_CONFIG,
  UPDATE_REQUIRED_DOCUMENT_CONFIG,
  DELETE_REQUIRED_DOCUMENT_CONFIG,
  UPDATE_ORGANIZATION_ONBOARDING_SIGNATURE_FORMS,
} from '../../../gql/mutations/organizationOnboardingMutations'
import {parseId} from '../../../helpers'
import {ModalAddSignatureForm} from '../../signatureForms/components/ModalAddSignatureForm'
import {
  ORG_MESSAGES as M,
  documentContextLabel,
  documentContextOptions,
  documentTypeLabel,
  documentTypeOptions,
  stepKeyLabel,
  stepKeyOptions,
} from '../organizationMessages'

interface Props {
  organizationId: string
}

interface IStepMessage {
  id: string
  stepKey: string
  message: string
  isActive: boolean
}

interface IRequiredDocument {
  id: string
  documentType: string
  context: string
  isRequired: boolean
}

interface ISignatureForm {
  id: string
  name: string
}

// Los valores por defecto son claves de estado que viajan a la API, no texto:
// se dejan literales en vez de leerlos del catalogo traducido.
const EMPTY_STEP_FORM = {stepKey: 'personal_info', message: '', isActive: true}
const EMPTY_DOC_FORM = {documentType: 'governmentId', context: 'licensed_yes', isRequired: true}

export const OrganizationOnboardingTab: React.FC<Props> = ({organizationId}) => {
  const intl = useIntl()
  const {data, loading, refetch} = useQuery(GET_ONBOARDING_CONFIG_BY_ORGANIZATION, {
    variables: {organization: organizationId},
    fetchPolicy: 'network-only',
  })

  const config = data?.onboardingConfigByOrganization
  const stepMessages: IStepMessage[] = config?.stepMessages || []
  const requiredDocuments: IRequiredDocument[] = config?.requiredDocuments || []
  const selectedSignatureFormIds: string[] = (config?.signatureForms || []).map((f: ISignatureForm) => f.id)

  return (
    <div>
      <Card className="mb-5">
        <Card.Body>
          <h5 className="fw-bold mb-3">{intl.formatMessage(M.stepMessages)}</h5>
          <StepMessagesPanel
            organizationId={organizationId}
            stepMessages={stepMessages}
            loading={loading}
            refetch={refetch}
          />
        </Card.Body>
      </Card>

      <Card className="mb-5">
        <Card.Body>
          <h5 className="fw-bold mb-3">{intl.formatMessage(M.requiredDocuments)}</h5>
          <RequiredDocumentsPanel
            organizationId={organizationId}
            requiredDocuments={requiredDocuments}
            loading={loading}
            refetch={refetch}
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <h5 className="fw-bold mb-3">{intl.formatMessage(M.agreements)}</h5>
          <AgreementsPanel
            organizationId={organizationId}
            selectedSignatureFormIds={selectedSignatureFormIds}
            refetch={refetch}
          />
        </Card.Body>
      </Card>
    </div>
  )
}

const StepMessagesPanel: React.FC<{
  organizationId: string
  stepMessages: IStepMessage[]
  loading: boolean
  refetch: () => void
}> = ({
  organizationId, stepMessages, loading, refetch}) => {
  const intl = useIntl()
  const [createStepMessage] = useMutation(CREATE_ONBOARDING_STEP_MESSAGE)
  const [updateStepMessage] = useMutation(UPDATE_ONBOARDING_STEP_MESSAGE)
  const [deleteStepMessage] = useMutation(DELETE_ONBOARDING_STEP_MESSAGE)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({...EMPTY_STEP_FORM})
  const [showNewRow, setShowNewRow] = useState(false)
  const [newForm, setNewForm] = useState({...EMPTY_STEP_FORM})
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const startEdit = (row: IStepMessage) => {
    setEditingId(row.id)
    setEditForm({stepKey: row.stepKey, message: row.message, isActive: row.isActive})
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditForm({...EMPTY_STEP_FORM})
  }

  const handleCreate = async () => {
    setSaving(true)
    try {
      const res = await createStepMessage({
        variables: {
          stepMessageData: {
            organizationConfig: organizationId,
            stepKey: newForm.stepKey,
            message: newForm.message,
            isActive: newForm.isActive,
          },
        },
      })
      const errors = res.data?.createOnboardingStepMessage?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success(intl.formatMessage(M.toastStepCreated))
        setNewForm({...EMPTY_STEP_FORM})
        setShowNewRow(false)
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || intl.formatMessage(M.errStepCreate))
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (id: string) => {
    setSaving(true)
    try {
      const res = await updateStepMessage({
        variables: {
          id: parseId(id),
          stepMessageData: {
            organizationConfig: organizationId,
            stepKey: editForm.stepKey,
            message: editForm.message,
            isActive: editForm.isActive,
          },
        },
      })
      const errors = res.data?.updateOnboardingStepMessage?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success(intl.formatMessage(M.toastStepUpdated))
        cancelEdit()
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || intl.formatMessage(M.errStepUpdate))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm(intl.formatMessage(M.confirmDeleteStep))) return
    setDeletingId(id)
    try {
      const res = await deleteStepMessage({variables: {id: parseId(id)}})
      const errors = res.data?.deleteOnboardingStepMessage?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success(intl.formatMessage(M.toastStepDeleted))
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || intl.formatMessage(M.errStepDelete))
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-6">
        <Spinner animation="border" size="sm" />
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        {!showNewRow && (
          <Button size="sm" variant="outline-primary" onClick={() => setShowNewRow(true)}>
            <i className="bi bi-plus-lg me-1"></i>
            {intl.formatMessage(M.addStepMessage)}
          </Button>
        )}
      </div>

      <Table hover responsive size="sm">
        <thead>
          <tr>
            <th style={{width: '20%'}}>{intl.formatMessage(M.thStep)}</th>
            <th style={{width: '45%'}}>{intl.formatMessage(M.thMessage)}</th>
            <th style={{width: '10%'}}>{intl.formatMessage(M.active)}</th>
            <th style={{width: '25%'}} className="text-end">{intl.formatMessage(M.actions)}</th>
          </tr>
        </thead>
        <tbody>
          {showNewRow && (
            <tr>
              <td>
                <Form.Select
                  size="sm"
                  value={newForm.stepKey}
                  onChange={(e) => setNewForm((p) => ({...p, stepKey: e.target.value}))}
                >
                  {stepKeyOptions(intl).map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </td>
              <td>
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  placeholder={intl.formatMessage(M.placeholderMessage)}
                  value={newForm.message}
                  onChange={(e) => setNewForm((p) => ({...p, message: e.target.value}))}
                />
              </td>
              <td>
                <Form.Check
                  type="switch"
                  checked={newForm.isActive}
                  onChange={(e) => setNewForm((p) => ({...p, isActive: e.target.checked}))}
                />
              </td>
              <td className="text-end">
                <Button size="sm" variant="success" className="me-1" onClick={handleCreate} disabled={saving}>
                  <i className="bi bi-check-lg"></i>
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  onClick={() => {
                    setShowNewRow(false)
                    setNewForm({...EMPTY_STEP_FORM})
                  }}
                  disabled={saving}
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              </td>
            </tr>
          )}
          {stepMessages.length === 0 && !showNewRow && (
            <tr>
              <td colSpan={4} className="text-center text-muted py-4">
                {intl.formatMessage(M.stepMessagesEmpty)}
              </td>
            </tr>
          )}
          {stepMessages.map((row) => (
            <tr key={row.id}>
              {editingId === row.id ? (
                <>
                  <td>
                    <Form.Select
                      size="sm"
                      value={editForm.stepKey}
                      onChange={(e) => setEditForm((p) => ({...p, stepKey: e.target.value}))}
                    >
                      {stepKeyOptions(intl).map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      size="sm"
                      value={editForm.message}
                      onChange={(e) => setEditForm((p) => ({...p, message: e.target.value}))}
                    />
                  </td>
                  <td>
                    <Form.Check
                      type="switch"
                      checked={editForm.isActive}
                      onChange={(e) => setEditForm((p) => ({...p, isActive: e.target.checked}))}
                    />
                  </td>
                  <td className="text-end">
                    <Button size="sm" variant="success" className="me-1" onClick={() => handleUpdate(row.id)} disabled={saving}>
                      <i className="bi bi-check-lg"></i>
                    </Button>
                    <Button size="sm" variant="light" onClick={cancelEdit} disabled={saving}>
                      <i className="bi bi-x-lg"></i>
                    </Button>
                  </td>
                </>
              ) : (
                <>
                  <td>{stepKeyLabel(intl, row.stepKey)}</td>
                  <td style={{whiteSpace: 'pre-wrap'}}>{row.message}</td>
                  <td>{row.isActive ? <span className="badge bg-success">{intl.formatMessage(M.yes)}</span> : <span className="badge bg-secondary">{intl.formatMessage(M.no)}</span>}</td>
                  <td className="text-end">
                    <Button size="sm" variant="light" className="me-1" onClick={() => startEdit(row)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      className="text-danger"
                      onClick={() => handleDelete(row.id)}
                      disabled={deletingId === row.id}
                    >
                      {deletingId === row.id ? <Spinner animation="border" size="sm" /> : <i className="bi bi-trash"></i>}
                    </Button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

const RequiredDocumentsPanel: React.FC<{
  organizationId: string
  requiredDocuments: IRequiredDocument[]
  loading: boolean
  refetch: () => void
}> = ({
  organizationId, requiredDocuments, loading, refetch}) => {
  const intl = useIntl()
  const [createRequiredDocument] = useMutation(CREATE_REQUIRED_DOCUMENT_CONFIG)
  const [updateRequiredDocument] = useMutation(UPDATE_REQUIRED_DOCUMENT_CONFIG)
  const [deleteRequiredDocument] = useMutation(DELETE_REQUIRED_DOCUMENT_CONFIG)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({...EMPTY_DOC_FORM})
  const [showNewRow, setShowNewRow] = useState(false)
  const [newForm, setNewForm] = useState({...EMPTY_DOC_FORM})
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const startEdit = (row: IRequiredDocument) => {
    setEditingId(row.id)
    setEditForm({documentType: row.documentType, context: row.context, isRequired: row.isRequired})
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditForm({...EMPTY_DOC_FORM})
  }

  const handleCreate = async () => {
    setSaving(true)
    try {
      const res = await createRequiredDocument({
        variables: {
          requiredDocumentData: {
            organizationConfig: organizationId,
            documentType: newForm.documentType,
            context: newForm.context,
            isRequired: newForm.isRequired,
          },
        },
      })
      const errors = res.data?.createRequiredDocumentConfig?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success(intl.formatMessage(M.toastDocCreated))
        setNewForm({...EMPTY_DOC_FORM})
        setShowNewRow(false)
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || intl.formatMessage(M.errDocCreate))
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (id: string) => {
    setSaving(true)
    try {
      const res = await updateRequiredDocument({
        variables: {
          id: parseId(id),
          requiredDocumentData: {
            organizationConfig: organizationId,
            documentType: editForm.documentType,
            context: editForm.context,
            isRequired: editForm.isRequired,
          },
        },
      })
      const errors = res.data?.updateRequiredDocumentConfig?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success(intl.formatMessage(M.toastDocUpdated))
        cancelEdit()
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || intl.formatMessage(M.errDocUpdate))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm(intl.formatMessage(M.confirmDeleteDoc))) return
    setDeletingId(id)
    try {
      const res = await deleteRequiredDocument({variables: {id: parseId(id)}})
      const errors = res.data?.deleteRequiredDocumentConfig?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success(intl.formatMessage(M.toastDocDeleted))
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || intl.formatMessage(M.errDocDelete))
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-6">
        <Spinner animation="border" size="sm" />
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        {!showNewRow && (
          <Button size="sm" variant="outline-primary" onClick={() => setShowNewRow(true)}>
            <i className="bi bi-plus-lg me-1"></i>
            {intl.formatMessage(M.addRequiredDocument)}
          </Button>
        )}
      </div>

      <Table hover responsive size="sm">
        <thead>
          <tr>
            <th style={{width: '35%'}}>{intl.formatMessage(M.thDocumentType)}</th>
            <th style={{width: '30%'}}>{intl.formatMessage(M.thContext)}</th>
            <th style={{width: '10%'}}>{intl.formatMessage(M.thRequired)}</th>
            <th style={{width: '25%'}} className="text-end">{intl.formatMessage(M.actions)}</th>
          </tr>
        </thead>
        <tbody>
          {showNewRow && (
            <tr>
              <td>
                <Form.Select
                  size="sm"
                  value={newForm.documentType}
                  onChange={(e) => setNewForm((p) => ({...p, documentType: e.target.value}))}
                >
                  {documentTypeOptions(intl).map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </Form.Select>
              </td>
              <td>
                <Form.Select
                  size="sm"
                  value={newForm.context}
                  onChange={(e) => setNewForm((p) => ({...p, context: e.target.value}))}
                >
                  {documentContextOptions(intl).map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Form.Select>
              </td>
              <td>
                <Form.Check
                  type="switch"
                  checked={newForm.isRequired}
                  onChange={(e) => setNewForm((p) => ({...p, isRequired: e.target.checked}))}
                />
              </td>
              <td className="text-end">
                <Button size="sm" variant="success" className="me-1" onClick={handleCreate} disabled={saving}>
                  <i className="bi bi-check-lg"></i>
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  onClick={() => {
                    setShowNewRow(false)
                    setNewForm({...EMPTY_DOC_FORM})
                  }}
                  disabled={saving}
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              </td>
            </tr>
          )}
          {requiredDocuments.length === 0 && !showNewRow && (
            <tr>
              <td colSpan={4} className="text-center text-muted py-4">
                {intl.formatMessage(M.documentsEmpty)}
              </td>
            </tr>
          )}
          {requiredDocuments.map((row) => (
            <tr key={row.id}>
              {editingId === row.id ? (
                <>
                  <td>
                    <Form.Select
                      size="sm"
                      value={editForm.documentType}
                      onChange={(e) => setEditForm((p) => ({...p, documentType: e.target.value}))}
                    >
                      {documentTypeOptions(intl).map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={editForm.context}
                      onChange={(e) => setEditForm((p) => ({...p, context: e.target.value}))}
                    >
                      {documentContextOptions(intl).map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Check
                      type="switch"
                      checked={editForm.isRequired}
                      onChange={(e) => setEditForm((p) => ({...p, isRequired: e.target.checked}))}
                    />
                  </td>
                  <td className="text-end">
                    <Button size="sm" variant="success" className="me-1" onClick={() => handleUpdate(row.id)} disabled={saving}>
                      <i className="bi bi-check-lg"></i>
                    </Button>
                    <Button size="sm" variant="light" onClick={cancelEdit} disabled={saving}>
                      <i className="bi bi-x-lg"></i>
                    </Button>
                  </td>
                </>
              ) : (
                <>
                  <td>{documentTypeLabel(intl, row.documentType)}</td>
                  <td>{documentContextLabel(intl, row.context)}</td>
                  <td>{row.isRequired ? <span className="badge bg-success">{intl.formatMessage(M.yes)}</span> : <span className="badge bg-secondary">{intl.formatMessage(M.no)}</span>}</td>
                  <td className="text-end">
                    <Button size="sm" variant="light" className="me-1" onClick={() => startEdit(row)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      className="text-danger"
                      onClick={() => handleDelete(row.id)}
                      disabled={deletingId === row.id}
                    >
                      {deletingId === row.id ? <Spinner animation="border" size="sm" /> : <i className="bi bi-trash"></i>}
                    </Button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

const AgreementsPanel: React.FC<{
  organizationId: string
  selectedSignatureFormIds: string[]
  refetch: () => void
}> = ({
  organizationId, selectedSignatureFormIds, refetch}) => {
  const intl = useIntl()
  const {data, loading, refetch: refetchForms} = useQuery(GET_SIGNATURE_FORMS_BY_ORGANIZATION, {
    variables: {organization: organizationId, status: 'ACTIVE'},
    fetchPolicy: 'network-only',
  })
  const [updateSignatureForms] = useMutation(UPDATE_ORGANIZATION_ONBOARDING_SIGNATURE_FORMS)

  const availableForms: ISignatureForm[] = data?.allSignatureForms?.edges?.map((e: any) => e.node) || []

  const [selected, setSelected] = useState<Set<string>>(new Set(selectedSignatureFormIds))
  const [saving, setSaving] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)

  useEffect(() => {
    setSelected(new Set(selectedSignatureFormIds))
  }, [selectedSignatureFormIds.join(',')])

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await updateSignatureForms({
        variables: {organization: organizationId, signatureFormIds: Array.from(selected)},
      })
      const errors = res.data?.updateOrganizationOnboardingSignatureForms?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success(intl.formatMessage(M.toastAgreementsUpdated))
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || intl.formatMessage(M.errAgreementsUpdate))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-6">
        <Spinner animation="border" size="sm" />
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <p className="text-muted mb-0" style={{fontSize: '0.8rem'}}>
          {intl.formatMessage(M.agreementsHint)}
        </p>
        <Button variant="outline-primary" size="sm" onClick={() => setShowAddForm(true)}>
          {intl.formatMessage(M.addConsent)}
        </Button>
      </div>
      {showAddForm && (
        <ModalAddSignatureForm
          keyboard={false}
          backdrop="static"
          isOpen={showAddForm}
          onClose={() => setShowAddForm(false)}
          refetch={() => {
            refetchForms()
          }}
          idFormEdit={null}
          organizationId={organizationId}
        />
      )}
      {availableForms.length === 0 ? (
        <p className="text-muted text-center py-4">{intl.formatMessage(M.noSignatureForms)}</p>
      ) : (
        <div className="mb-4">
          {availableForms.map((form) => (
            <Form.Check
              key={form.id}
              type="checkbox"
              id={`signature-form-${form.id}`}
              label={form.name}
              checked={selected.has(form.id)}
              onChange={() => toggle(form.id)}
              className="mb-2"
            />
          ))}
        </div>
      )}
      <div className="d-flex justify-content-end">
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {intl.formatMessage(M.saving)}
            </>
          ) : (
            intl.formatMessage(M.save)
          )}
        </Button>
      </div>
    </div>
  )
}
